import React, { useState, useEffect } from 'react';
import { airtableService } from '../services/airtable';

const CheckOut = () => {
  const [activeVisitors, setActiveVisitors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    loadActiveVisitors();
  }, []);

  const loadActiveVisitors = async () => {
    setLoading(true);
    const visitors = await airtableService.getActiveVisitors();
    setActiveVisitors(visitors);
    setLoading(false);
  };

  const handleCheckOut = async (visitor) => {
    setCheckingOut(visitor.id);
    
    const result = await airtableService.checkOutVisitor(visitor.id);
    
    if (result) {
      // Remove from active visitors list
      setActiveVisitors(activeVisitors.filter(v => v.id !== visitor.id));
      alert(`${visitor.fields.Name} has been successfully checked out.`);
    } else {
      alert('Error checking out visitor. Please try again.');
    }
    
    setCheckingOut(null);
  };

  const filteredVisitors = activeVisitors.filter(visitor =>
    visitor.fields.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (dateString) => {
    if (!dateString) return 'No time';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid time';
      
      return date.toLocaleString('en-US', { 
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Time error';
    }
  };

  const calculateDuration = (checkInTime) => {
    const checkIn = new Date(checkInTime);
    const now = new Date();
    const diffMs = now - checkIn;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-gray-800">Visitor Check-Out</h1>
            <button
              onClick={loadActiveVisitors}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name..."
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
            />
            <div className="absolute left-3 top-4">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium text-gray-700">
              Active Visitors: <span className="text-blue-600 font-bold">{activeVisitors.length}</span>
            </span>
            <span className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>

        {/* Visitors List */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-lg text-gray-600">Loading active visitors...</div>
            </div>
          ) : filteredVisitors.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👋</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {activeVisitors.length === 0 ? 'No Active Visitors' : 'No Visitors Found'}
              </h3>
              <p className="text-gray-500">
                {activeVisitors.length === 0 
                  ? 'All visitors have been checked out.'
                  : 'Try searching with a different name.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredVisitors.map((visitor) => (
                <div
                  key={visitor.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-xl font-bold text-gray-800">
                          {visitor.fields.Name}
                        </h3>
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          Duration: {calculateDuration(visitor.fields['Check In Time'])}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-600">Reason:</span>
                          <p className="text-gray-800">{visitor.fields['Reason for Visit']}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600">Check-In Time:</span>
                          <p className="text-gray-800">{formatTime(visitor.fields['Check In Time'])}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-6">
                      <button
                        onClick={() => handleCheckOut(visitor)}
                        disabled={checkingOut === visitor.id}
                        className={`px-6 py-3 rounded-lg font-medium text-lg transition-colors ${
                          checkingOut === visitor.id
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        {checkingOut === visitor.id ? 'Checking Out...' : 'Check Out'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-xl">
          <p className="text-sm text-gray-600 text-center">
            Select a visitor above to complete their check-out process
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckOut;
